import type { Time } from 'lightweight-charts';

export interface TrendDrawing {
  id: string;
  type: 'trend';
  t1: Time;
  p1: number;
  t2: Time;
  p2: number;
}

export interface HLineDrawing {
  id: string;
  type: 'hline';
  price: number;
}

export type Drawing = TrendDrawing | HLineDrawing;

interface Props {
  drawings: Drawing[];
  pending: { time: Time; price: number } | null;
  hover: { x: number; y: number } | null;
  width: number;
  toX: (time: Time) => number | null;
  toY: (price: number) => number | null;
  trendColor?: string;
  hlineColor?: string;
  labelTextColor?: string;
}

/**
 * Renders all user drawings (trend lines + horizontal levels) inside the
 * chart overlay SVG. Coordinates are resolved through the lightweight-charts
 * scales so the shapes always follow scroll / zoom.
 */
export default function ChartDrawingLayer({
  drawings,
  pending,
  hover,
  width,
  toX,
  toY,
  trendColor,
  hlineColor,
  labelTextColor,
}: Props) {
  return (
    <>
      {drawings.map((drawing) => {
        if (drawing.type === 'hline') {
          const y = toY(drawing.price);
          if (y == null) return null;
          return (
            <g key={drawing.id}>
              <line x1={0} y1={y} x2={width} y2={y} stroke={hlineColor} strokeWidth={1} strokeDasharray="5 4" />
              <rect x={width - 62} y={y - 8} width={58} height={16} rx={3} fill={hlineColor} opacity={0.92} />
              <text x={width - 33} y={y + 4} textAnchor="middle" fontSize="10" fill={labelTextColor}>
                {drawing.price.toLocaleString('en-US', { maximumFractionDigits: 2 })}
              </text>
            </g>
          );
        }

        const x1 = toX(drawing.t1);
        const y1 = toY(drawing.p1);
        const x2 = toX(drawing.t2);
        const y2 = toY(drawing.p2);
        if (x1 == null || y1 == null || x2 == null || y2 == null) return null;

        return (
          <g key={drawing.id}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={trendColor} strokeWidth={1.5} />
            <circle cx={x1} cy={y1} r={3} fill={trendColor} />
            <circle cx={x2} cy={y2} r={3} fill={trendColor} />
          </g>
        );
      })}

      {pending && (
        (() => {
          const x1 = toX(pending.time);
          const y1 = toY(pending.price);
          if (x1 == null || y1 == null) return null;
          const x2 = hover ? hover.x : x1;
          const y2 = hover ? hover.y : y1;
          return (
            <g>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={trendColor} strokeWidth={1.5} strokeDasharray="4 3" />
              <circle cx={x1} cy={y1} r={3.5} fill={trendColor} />
            </g>
          );
        })()
      )}
    </>
  );
}